import React, {useMemo} from 'react';

import {InlineAlert} from '@/components/alerts';
import ChartCellHandler from './ChartCellHandler';
import WordCloudCellHandler from './WordCloudCellHandler';
import TextSamplesCellHandler from './TextSamplesCellHandler';
import ConcordanceCellHandler from './ConcordanceCellHandler';
import CommentCellHandler from './CommentCellHandler';
import BigNumberCellHandler from './BigNumberCellHandler';
import TableCellHandler from './TableCellHandler';
import {ChartQuery, ChartSettings, BarOrientation, BarSortOrder} from 'entities/chart';
import {resolveDataLabelPosition} from '@/views/storyboard/utils/chartDataLabels';
import {ChartType} from '@/views/storyboard/utils/chartConstants';
import TrendingWordsCellHandler from './TrendingWordsCellHandler';
import PivotCellHandler from './PivotCellHandler';
import i18n from 'i18n';
import {ChartFooterData} from '@/views/storyboard/charts/utils/chartFooterData';


const cellHandlers = {
    [ChartType.BarChart]: ChartCellHandler,
    [ChartType.LineChart]: ChartCellHandler,
    [ChartType.AreaChart]: ChartCellHandler,
    [ChartType.PieChart]: ChartCellHandler,
    [ChartType.SunburstChart]: ChartCellHandler,
    [ChartType.TreemapChart]: ChartCellHandler,
    [ChartType.Table]: TableCellHandler,
    [ChartType.PivotTable]: PivotCellHandler,
    [ChartType.BigNumber]: BigNumberCellHandler,
    [ChartType.Wordcloud]: WordCloudCellHandler,

    [ChartType.TextSamples]: TextSamplesCellHandler,
    [ChartType.TrendingWords]: TrendingWordsCellHandler,
    [ChartType.Concordance]: ConcordanceCellHandler,

    [ChartType.Comment]: CommentCellHandler,
};

interface Props {
    chart: ChartSettings;
    chartQuery: ChartQuery;
    chartRef?: any; // TODO
    previewOnly: boolean;
    chartSettingsView?: boolean;
    onPreviewFooterChange?: (footerData: ChartFooterData) => void;
}

const CellHandlerContainer = React.memo<Props>((
    {
        chart,
        chartQuery,
        chartRef,
        previewOnly,
        chartSettingsView,
        onPreviewFooterChange,
    }
) => {
    const CellHandler = cellHandlers[chart.type];

    const filteredChartQuery = useMemo(() => ({
        ...chartQuery,
        additionalFilters: Array.isArray(chartQuery.additionalFilters) 
            ? chartQuery.additionalFilters.map(filter => {
                const {description, ...rest} = filter;
                return rest;
            })
            : []
    }), [chartQuery]);


    try {
        return (
            <CellHandler
                chartId={chart.id}
                chartType={chart.type}
                chartHeight={chart.height}
                chartWidth={chart.width}
                chartTitle={chart.title}
                chartDescription={chart.description ?? ''}
                chartRef={chartRef}
                linearRegression={chart.linearRegression}
                comparablePeriod={chart.comparablePeriod}
                movingAverageDays={chart.movingAverageDays}
                movingAverageDaysCenter={chart.movingAverageDaysCenter}
                scalingFactor={chart.scalingFactor}
                barOrientation={chart.barOrientation ?? BarOrientation.Vertical}
                barSortOrder={chart.barSortOrder ?? BarSortOrder.HighToLow}
                dataLabelPosition={resolveDataLabelPosition(chart.type, chart.dataLabelPosition)}
                chartQuery={filteredChartQuery}
                previewOnly={previewOnly}

                i18n={i18n}
                t={i18n.t}
                tReady={true}
                chartSettingsView={chartSettingsView}
                onPreviewFooterChange={onPreviewFooterChange}
            />
        );
    } catch (e) {
        console.error('Chart failed to render', e, chart, chartQuery);

        return (
            <InlineAlert 
                variant="error" 
                message={i18n.t('jt_something_went.text')}
            />
        );
    }
});


export default CellHandlerContainer;
